import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import {
  RatingContainer,
  RatingText,
  RatingsStar,
  SpanStyle,
  VoiceOnlyText,
} from './Rating.styles';
import { NoRatingText } from './SnackCard.styles';

type Props = {
  hideNumber?: boolean;
  outOf?: number;
  rating?: number;
  size?: number;
  navigateToSnackDetails?: () => void;
  surveyCount?: number;
};

export const Rating = ({
  hideNumber,
  outOf,
  rating,
  navigateToSnackDetails,
  surveyCount,
}: Props) => {
  const { t } = useTranslation();

  const surveyRating = surveyCount ? ` (${surveyCount})` : '';
  const roundedRating = Math.round(rating || 0);
  const isGEHAV2 = shouldUseGehaV2Styling();

  if (rating === undefined || rating === null) {
    return (
      <NoRatingText
        color={isGEHAV2 ? '$core.color.brand.100' : '$neutralGray'}
        cssProps={{ isGEHAV2 }}
        data-testid="rating-container"
      >
        {t('Ratings not available')}
      </NoRatingText>
    );
  }

  const ratingTextComponent = (
    <RatingText color="$core.color.brand.60">
      <SpanStyle aria-hidden="true">
        {`${rating.toFixed(1)}${surveyRating}`}
      </SpanStyle>
      <VoiceOnlyText>{`${rating.toFixed(1)} stars`}</VoiceOnlyText>
    </RatingText>
  );

  return (
    <RatingContainer
      alignContent="center"
      alignItems="center"
      data-auto-testid="rating-container"
      data-testid="rating-container"
      onClick={navigateToSnackDetails}
    >
      <Flex.Content>
        {[...new Array(outOf)].map((item, index) => {
          const starNum: number = index + 1;
          const filled: boolean = starNum <= roundedRating;
          return (
            <RatingsStar
              data-testid={`rating-star-${filled}`}
              fontWeight="normal"
              key={`rating-star-${starNum}`}
              size="$extraSmallText"
              variant="native"
            >
              <IconSymbol
                color={filled ? '$core.color.yellow.100' : 'black'}
                icon="star_rate"
                size="20"
                variant={filled ? 'filled' : 'outlined'}
              />
            </RatingsStar>
          );
        })}
      </Flex.Content>
      <Flex.Content>{!hideNumber ? ratingTextComponent : null}</Flex.Content>
    </RatingContainer>
  );
};
